import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';

/** A key of the app: a combination such as `/`, `f`, `?`, `mod+k`, `alt+s`. */
export interface Shortcut {
  keys: string;
  /** Returns false to leave the key to the next shortcut. */
  handler: (event: KeyboardEvent) => boolean | void;
  /** The higher one wins when two register the same keys, e.g. `/`. */
  priority?: number;
  /** Also while the focus is in a field, as `mod+k`. */
  inFields?: boolean;
}

/** Whether a key press goes to a field the user types in. */
export const isTyping = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  return (
    !!element &&
    !!element.tagName &&
    (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) ||
      element.isContentEditable)
  );
};

/**
 * The combination of a key press: `mod` stands for Ctrl and Cmd; with Alt the
 * physical key counts, because Alt changes the character on a Mac.
 */
export const keyCombo = (event: KeyboardEvent) => {
  const key =
    event.altKey && /^Key[A-Z]$/.test(event.code ?? '')
      ? event.code.slice(3).toLowerCase()
      : (event.key ?? '').length === 1
        ? event.key.toLowerCase()
        : (event.key ?? '');
  return [
    event.ctrlKey || event.metaKey ? 'mod' : '',
    event.altKey ? 'alt' : '',
    key,
  ]
    .filter(Boolean)
    .join('+');
};

/**
 * One keydown listener for the shortcuts of the app. Pages and components
 * register their keys while they are shown; outside fields only, unless a
 * shortcut says otherwise.
 */
@Injectable({ providedIn: 'root' })
export class ShortcutService implements OnDestroy {
  private document = inject(DOCUMENT);
  private shortcuts: Shortcut[] = [];
  private listener = (event: KeyboardEvent) => this.onKeydown(event);

  constructor() {
    this.document.addEventListener('keydown', this.listener);
  }

  ngOnDestroy() {
    this.document.removeEventListener('keydown', this.listener);
  }

  /** Adds a shortcut; the returned function removes it again. */
  register(shortcut: Shortcut): () => void {
    this.shortcuts = [...this.shortcuts, shortcut];
    return () => {
      this.shortcuts = this.shortcuts.filter((x) => x !== shortcut);
    };
  }

  onKeydown(event: KeyboardEvent) {
    if (event.defaultPrevented || event.isComposing) {
      return;
    }
    const combo = keyCombo(event);
    const typing = isTyping(event.target);
    // The highest priority first, then the last registered: the page over
    // the shell.
    const candidates = this.shortcuts
      .map((shortcut, index) => ({ shortcut, index }))
      .filter(({ shortcut: x }) => x.keys === combo && (!typing || x.inFields))
      .sort(
        (a, b) =>
          (b.shortcut.priority ?? 0) - (a.shortcut.priority ?? 0) ||
          b.index - a.index
      );
    for (const { shortcut } of candidates) {
      if (shortcut.handler(event) !== false) {
        event.preventDefault();
        return;
      }
    }
  }
}
